/**
 * Client Modal
 * Modal for creating/editing clients
 */

import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useClientStore } from '../store/clientStore';
import { PROJECT_PRICING, calculateRetainer, getComplexityOptions } from '../config/projectPricing';
import './Modal.css';

const ClientModal = ({ client, onClose }) => {
  const user = useAuthStore((state) => state.user);
  const createClient = useClientStore((state) => state.createClient);
  const updateClient = useClientStore((state) => state.updateClient);

  const [formData, setFormData] = useState({
    name: client?.name || '',
    email: client?.email || '',
    phone: client?.phone || '',
    company: client?.company || '',
    defaultRate: client?.defaultRate || '',
    currency: client?.currency || 'USD',
    address: {
      street: client?.address?.street || '',
      city: client?.address?.city || '',
      state: client?.address?.state || '',
      zip: client?.address?.zip || '',
      country: client?.address?.country || '',
    },
    notes: client?.notes || '',
    // Pricing fields (YouTube model: build fee + 20% retainer)
    pricingTier: client?.pricingTier || 'GLOBAL',
    complexity: client?.complexity || 'medium',
    buildFee: client?.buildFee || '',
    monthlyRetainer: client?.monthlyRetainer || 0,
    discountPercent: client?.discountPercent || 0,
    // Manual retainer override (for special agreements)
    manualRetainerOverride: client?.manualRetainerOverride || false,
    // Graduated discount schedule (growth-friendly pricing)
    graduatedDiscount: {
      enabled: client?.graduatedDiscount?.enabled || false,
      year1: client?.graduatedDiscount?.year1 || 80,  // 80% off Year 1
      year2: client?.graduatedDiscount?.year2 || 75,  // 75% off Year 2
      year3: client?.graduatedDiscount?.year3 || 70,  // 70% off Year 3
      year4: client?.graduatedDiscount?.year4 || 65,  // 65% off Year 4
      year5Plus: client?.graduatedDiscount?.year5Plus || 60,  // 60% off Year 5+
      startDate: client?.graduatedDiscount?.startDate || new Date().toISOString().split('T')[0],
    },
    lateFeeConfig: {
      enabled: client?.lateFeeConfig?.enabled || false,
      type: client?.lateFeeConfig?.type || 'percentage',
      amount: client?.lateFeeConfig?.amount || 5,
      gracePeriodDays: client?.lateFeeConfig?.gracePeriodDays || 7,
      maxFee: client?.lateFeeConfig?.maxFee || '',
    },
    // Invoice & Billing Configuration
    billingConfig: {
      // Monthly infrastructure billing type
      monthlyBillingType: client?.billingConfig?.monthlyBillingType || 'actual_plus_percent',
      monthlyFixedAmount: client?.billingConfig?.monthlyFixedAmount || '',
      managementFeePercent: client?.billingConfig?.managementFeePercent ?? 20,
      // Payment options for invoices
      paymentOptionsEnabled: client?.billingConfig?.paymentOptionsEnabled || false,
      paymentOptions: client?.billingConfig?.paymentOptions || [
        { id: 'full', name: 'Full Payment', installments: 1, enabled: true },
        { id: '3month', name: '3-Month Installment', installments: 3, enabled: false },
        { id: '6month', name: '6-Month Installment', installments: 6, enabled: false },
      ],
      // Invoice email settings
      invoiceCcEmails: client?.billingConfig?.invoiceCcEmails || ['roseller@fusionict.com'],
    },
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPricing, setShowPricing] = useState(client?.buildFee > 0 || false);
  const [showBillingConfig, setShowBillingConfig] = useState(client?.billingConfig?.paymentOptionsEnabled || false);

  // Auto-calculate retainer when build fee or discount changes (unless manual override)
  // Retainer = 20% of DISCOUNTED build fee
  useEffect(() => {
    if (formData.manualRetainerOverride) return; // Skip auto-calculation if manual override
    const buildFee = parseFloat(formData.buildFee) || 0;
    const discount = parseFloat(formData.discountPercent) || 0;
    const discountedBuildFee = buildFee * (1 - discount / 100);
    const retainer = calculateRetainer(discountedBuildFee);
    setFormData(prev => ({
      ...prev,
      monthlyRetainer: retainer,
    }));
  }, [formData.buildFee, formData.discountPercent, formData.manualRetainerOverride]);

  // Update currency when pricing tier changes
  useEffect(() => {
    const tier = PROJECT_PRICING.TIERS[formData.pricingTier];
    if (tier && !client) {
      setFormData(prev => ({
        ...prev,
        currency: tier.currency,
      }));
    }
  }, [formData.pricingTier, client]);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Convert numeric fields
      const clientData = {
        ...formData,
        defaultRate: parseFloat(formData.defaultRate) || 0,
        buildFee: parseFloat(formData.buildFee) || 0,
        monthlyRetainer: parseFloat(formData.monthlyRetainer) || 0,
        discountPercent: parseFloat(formData.discountPercent) || 0,
        graduatedDiscount: formData.graduatedDiscount.enabled ? {
          ...formData.graduatedDiscount,
          year1: parseFloat(formData.graduatedDiscount.year1) || 80,
          year2: parseFloat(formData.graduatedDiscount.year2) || 75,
          year3: parseFloat(formData.graduatedDiscount.year3) || 70,
          year4: parseFloat(formData.graduatedDiscount.year4) || 65,
          year5Plus: parseFloat(formData.graduatedDiscount.year5Plus) || 60,
        } : null,
        billingConfig: {
          ...formData.billingConfig,
          monthlyFixedAmount: parseFloat(formData.billingConfig.monthlyFixedAmount) || 0,
          managementFeePercent: parseFloat(formData.billingConfig.managementFeePercent) || 20,
          invoiceCcEmails: formData.billingConfig.invoiceCcEmails.filter(email => email.trim() !== ''),
        },
      };

      if (client) {
        await updateClient(client.id, clientData);
      } else {
        await createClient(user.uid, clientData);
      }

      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  const handleLateFeeChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      lateFeeConfig: {
        ...prev.lateFeeConfig,
        [field]: value,
      },
    }));
  };

  const handleGraduatedDiscountChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      graduatedDiscount: {
        ...prev.graduatedDiscount,
        [field]: value,
      },
    }));
  };

  const handleBillingConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      billingConfig: {
        ...prev.billingConfig,
        [field]: value,
      },
    }));
  };

  const handlePaymentOptionToggle = (optionId) => {
    setFormData(prev => ({
      ...prev,
      billingConfig: {
        ...prev.billingConfig,
        paymentOptions: prev.billingConfig.paymentOptions.map(opt =>
          opt.id === optionId ? { ...opt, enabled: !opt.enabled } : opt
        ),
      },
    }));
  };

  const handleAddCcEmail = () => {
    setFormData(prev => ({
      ...prev,
      billingConfig: {
        ...prev.billingConfig,
        invoiceCcEmails: [...prev.billingConfig.invoiceCcEmails, ''],
      },
    }));
  };

  const handleRemoveCcEmail = (index) => {
    setFormData(prev => ({
      ...prev,
      billingConfig: {
        ...prev.billingConfig,
        invoiceCcEmails: prev.billingConfig.invoiceCcEmails.filter((_, i) => i !== index),
      },
    }));
  };

  const handleCcEmailChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      billingConfig: {
        ...prev.billingConfig,
        invoiceCcEmails: prev.billingConfig.invoiceCcEmails.map((email, i) =>
          i === index ? value : email
        ),
      },
    }));
  };

  // Calculate the current year of the client based on start date
  const getCurrentYear = () => {
    if (!formData.graduatedDiscount.startDate) return 1;
    const startDate = new Date(formData.graduatedDiscount.startDate);
    const now = new Date();
    const yearsDiff = Math.floor((now - startDate) / (365.25 * 24 * 60 * 60 * 1000));
    return Math.max(1, Math.min(5, yearsDiff + 1));
  };

  // Get the discount percentage for a given year
  const getDiscountForYear = (year) => {
    const gd = formData.graduatedDiscount;
    if (year === 1) return gd.year1;
    if (year === 2) return gd.year2;
    if (year === 3) return gd.year3;
    if (year === 4) return gd.year4;
    return gd.year5Plus;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{client ? 'Edit Client' : 'Add Client'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
          <div className="form-group">
            <label htmlFor="name">Client Name *</label>
            <input
              id="name"
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                id="email"
                type="email"
                className="input"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                id="phone"
                type="tel"
                className="input"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="company">Company</label>
            <input
              id="company"
              type="text"
              className="input"
              value={formData.company}
              onChange={(e) => handleChange('company', e.target.value)}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="defaultRate">Default Hourly Rate</label>
              <input
                id="defaultRate"
                type="number"
                step="0.01"
                min="0"
                className="input"
                value={formData.defaultRate}
                onChange={(e) => handleChange('defaultRate', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="currency">Currency</label>
              <select
                id="currency"
                className="input"
                value={formData.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="PHP">PHP</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="street">Street Address</label>
            <input
              id="street"
              type="text"
              className="input"
              value={formData.address.street}
              onChange={(e) => handleAddressChange('street', e.target.value)}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                id="city"
                type="text"
                className="input"
                value={formData.address.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="state">State</label>
              <input
                id="state"
                type="text"
                className="input"
                value={formData.address.state}
                onChange={(e) => handleAddressChange('state', e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="zip">ZIP Code</label>
              <input
                id="zip"
                type="text"
                className="input"
                value={formData.address.zip}
                onChange={(e) => handleAddressChange('zip', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="country">Country</label>
              <input
                id="country"
                type="text"
                className="input"
                value={formData.address.country}
                onChange={(e) => handleAddressChange('country', e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              className="input"
              rows="3"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any additional notes about this client..."
            />
          </div>

          {/* Pricing Configuration */}
          <div className="form-section">
            <button
              type="button"
              className="section-toggle"
              onClick={() => setShowPricing(!showPricing)}
            >
              <span>{showPricing ? '▼' : '▶'} Pricing & Retainer</span>
              <span className="toggle-hint">
                {formData.buildFee > 0
                  ? (() => {
                      const buildFee = Math.round(parseFloat(formData.buildFee) || 0);
                      const retainer = Math.round(formData.monthlyRetainer);

                      if (formData.graduatedDiscount.enabled) {
                        const currentYearDiscount = getDiscountForYear(getCurrentYear());
                        const currentRetainer = Math.round(retainer * (1 - currentYearDiscount / 100));
                        return `${formData.currency} ${buildFee.toLocaleString()} build / ${formData.currency} ${currentRetainer.toLocaleString()}/mo (Graduated: Year ${getCurrentYear()})`;
                      }

                      const discount = parseFloat(formData.discountPercent) || 0;
                      const finalBuildFee = Math.round(buildFee * (1 - discount / 100));
                      // If manual override, don't apply discount to retainer
                      const finalRetainer = formData.manualRetainerOverride
                        ? retainer
                        : Math.round(retainer * (1 - discount / 100));
                      if (formData.manualRetainerOverride) {
                        return `${formData.currency} ${finalBuildFee.toLocaleString()} build / ${formData.currency} ${retainer.toLocaleString()}/mo (custom)`;
                      }
                      return discount > 0
                        ? `${formData.currency} ${finalBuildFee.toLocaleString()} build / ${formData.currency} ${finalRetainer.toLocaleString()}/mo (${discount}% off)`
                        : `${formData.currency} ${buildFee.toLocaleString()} build / ${formData.currency} ${retainer.toLocaleString()}/mo`;
                    })()
                  : 'Not configured'}
              </span>
            </button>

            {showPricing && (
              <div className="section-content">
                <p className="section-description">
                  Set the build fee for this project. Monthly retainer is automatically calculated as 20% of the build fee.
                </p>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="pricingTier">Pricing Tier</label>
                    <select
                      id="pricingTier"
                      className="input"
                      value={formData.pricingTier}
                      onChange={(e) => handleChange('pricingTier', e.target.value)}
                    >
                      <option value="GLOBAL">Global (USD)</option>
                      <option value="PH">Philippines (PHP)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="complexity">Complexity</label>
                    <select
                      id="complexity"
                      className="input"
                      value={formData.complexity}
                      onChange={(e) => {
                        const complexity = e.target.value;
                        const tier = PROJECT_PRICING.TIERS[formData.pricingTier];
                        const suggestedFee = tier?.complexityLevels?.[complexity]?.buildFee || '';
                        handleChange('complexity', complexity);
                        if (suggestedFee && !formData.buildFee) {
                          handleChange('buildFee', suggestedFee);
                        }
                      }}
                    >
                      {getComplexityOptions(formData.pricingTier).map(opt => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label} ({opt.formattedBuildFee})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="buildFee">Build Fee (One-time)</label>
                    <input
                      id="buildFee"
                      type="number"
                      step="0.01"
                      min="0"
                      className="input"
                      value={formData.buildFee}
                      onChange={(e) => handleChange('buildFee', e.target.value)}
                      placeholder={`e.g., ${PROJECT_PRICING.TIERS[formData.pricingTier]?.complexityLevels?.medium?.buildFee || '2000'}`}
                    />
                    <span className="help-text">Enter the full build fee amount</span>
                  </div>

                  {!formData.graduatedDiscount.enabled && (
                    <div className="form-group">
                      <label htmlFor="discountPercent">Discount (%)</label>
                      <input
                        id="discountPercent"
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        className="input"
                        value={formData.discountPercent}
                        onChange={(e) => handleChange('discountPercent', e.target.value)}
                        placeholder="0"
                      />
                      <span className="help-text">Applies to both build fee and retainer</span>
                    </div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="monthlyRetainer">
                      Monthly Retainer {!formData.manualRetainerOverride && '(20%)'}
                    </label>
                    <input
                      id="monthlyRetainer"
                      type={formData.manualRetainerOverride ? 'number' : 'text'}
                      step="0.01"
                      min="0"
                      className="input"
                      value={formData.manualRetainerOverride
                        ? formData.monthlyRetainer
                        : (formData.monthlyRetainer > 0 ? `${formData.currency} ${formData.monthlyRetainer.toLocaleString()}` : '')}
                      disabled={!formData.manualRetainerOverride}
                      placeholder={formData.manualRetainerOverride ? 'Enter custom amount' : 'Auto-calculated'}
                      onChange={(e) => handleChange('monthlyRetainer', parseFloat(e.target.value) || 0)}
                    />
                    <span className="help-text">
                      {formData.manualRetainerOverride
                        ? 'Custom monthly retainer amount'
                        : '20% of discounted build fee, then discount applied'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '1.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
                      <input
                        type="checkbox"
                        style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
                        checked={!!formData.manualRetainerOverride}
                        onChange={(e) => handleChange('manualRetainerOverride', e.target.checked)}
                      />
                      <span>Manual Override</span>
                    </label>
                  </div>
                </div>

                {formData.buildFee > 0 && !formData.graduatedDiscount.enabled && (
                  <div className="pricing-preview">
                    <strong>Pricing Summary:</strong>
                    {(() => {
                      const buildFee = Math.round(parseFloat(formData.buildFee) || 0);
                      const discount = parseFloat(formData.discountPercent) || 0;
                      const finalBuildFee = Math.round(buildFee * (1 - discount / 100));
                      const retainer = Math.round(formData.monthlyRetainer);
                      // If manual override, don't apply discount to retainer - user specifies final amount
                      const finalRetainer = formData.manualRetainerOverride
                        ? retainer
                        : Math.round(retainer * (1 - discount / 100));
                      return (
                        <ul>
                          {discount > 0 ? (
                            <>
                              <li>Build Fee: {formData.currency} {buildFee.toLocaleString()} <span style={{color: '#22c55e'}}>- {discount}% = {formData.currency} {finalBuildFee.toLocaleString()}</span> (one-time)</li>
                              {formData.manualRetainerOverride ? (
                                <li>Monthly Retainer: {formData.currency} {retainer.toLocaleString()}/month <span style={{color: '#3b82f6'}}>(manual override)</span></li>
                              ) : (
                                <li>Monthly Retainer: {formData.currency} {retainer.toLocaleString()} <span style={{color: '#22c55e'}}>- {discount}% = {formData.currency} {finalRetainer.toLocaleString()}</span>/month</li>
                              )}
                              <li>Annual Retainer: {formData.currency} {(finalRetainer * 12).toLocaleString()}/year</li>
                            </>
                          ) : (
                            <>
                              <li>Build Fee: {formData.currency} {buildFee.toLocaleString()} (one-time)</li>
                              <li>Monthly Retainer: {formData.currency} {retainer.toLocaleString()}/month{formData.manualRetainerOverride && <span style={{color: '#3b82f6'}}> (manual override)</span>}</li>
                              <li>Annual Retainer: {formData.currency} {(retainer * 12).toLocaleString()}/year</li>
                            </>
                          )}
                        </ul>
                      );
                    })()}
                  </div>
                )}

                {/* Graduated Discount Schedule */}
                <div className="graduated-discount-section" style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.graduatedDiscount.enabled}
                        onChange={(e) => handleGraduatedDiscountChange('enabled', e.target.checked)}
                      />
                      <span><strong>Enable Graduated Discount Schedule</strong></span>
                    </label>
                    <span className="help-text" style={{ marginLeft: '1.5rem', display: 'block' }}>
                      Growth-friendly pricing: discount decreases annually as client's business grows
                    </span>
                  </div>

                  {formData.graduatedDiscount.enabled && (
                    <>
                      <div className="form-group" style={{ marginTop: '1rem' }}>
                        <label htmlFor="graduatedStartDate">Billing Start Date</label>
                        <input
                          id="graduatedStartDate"
                          type="date"
                          className="input"
                          value={formData.graduatedDiscount.startDate}
                          onChange={(e) => handleGraduatedDiscountChange('startDate', e.target.value)}
                        />
                        <span className="help-text">Year 1 starts from this date</span>
                      </div>

                      {formData.buildFee > 0 && (
                        <div className="graduated-preview" style={{ marginTop: '1rem' }}>
                          <strong>5-Year Pricing Projection:</strong>
                          <table style={{ width: '100%', marginTop: '0.5rem', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#e9ecef' }}>
                                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #dee2e6' }}>Year</th>
                                <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #dee2e6' }}>Discount</th>
                                <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #dee2e6' }}>Monthly</th>
                                <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #dee2e6' }}>Annual</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[1, 2, 3, 4, 5].map((year) => {
                                const baseRetainer = parseFloat(formData.monthlyRetainer) || 0;
                                const discount = getDiscountForYear(year);
                                const monthlyFee = Math.round(baseRetainer * (1 - discount / 100));
                                const annualFee = monthlyFee * 12;
                                const currentYear = getCurrentYear();
                                const isCurrentYear = year === currentYear;
                                return (
                                  <tr key={year} style={{ backgroundColor: isCurrentYear ? '#d4edda' : 'white' }}>
                                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                      Year {year}{year === 5 ? '+' : ''} {isCurrentYear && <span style={{ color: '#28a745', fontWeight: 'bold' }}>(Current)</span>}
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #dee2e6', color: '#22c55e', fontWeight: 'bold' }}>
                                      {discount}% off
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #dee2e6' }}>
                                      {formData.currency} {monthlyFee.toLocaleString()}
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #dee2e6' }}>
                                      {formData.currency} {annualFee.toLocaleString()}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6c757d' }}>
                            Base retainer: {formData.currency} {Math.round(formData.monthlyRetainer).toLocaleString()}/month (20% of build fee)
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Late Fee Configuration */}
          <div className="form-section">
            <button
              type="button"
              className="section-toggle"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <span>{showAdvanced ? '▼' : '▶'} Late Fee Settings</span>
              <span className="toggle-hint">
                {formData.lateFeeConfig.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </button>

            {showAdvanced && (
              <div className="section-content">
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.lateFeeConfig.enabled}
                      onChange={(e) => handleLateFeeChange('enabled', e.target.checked)}
                    />
                    <span>Enable automatic late fees for this client</span>
                  </label>
                </div>

                {formData.lateFeeConfig.enabled && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="lateFeeType">Fee Type</label>
                        <select
                          id="lateFeeType"
                          className="input"
                          value={formData.lateFeeConfig.type}
                          onChange={(e) => handleLateFeeChange('type', e.target.value)}
                        >
                          <option value="percentage">Percentage of Invoice</option>
                          <option value="flat">Flat Amount</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="lateFeeAmount">
                          {formData.lateFeeConfig.type === 'percentage' ? 'Percentage (%)' : 'Amount'}
                        </label>
                        <input
                          id="lateFeeAmount"
                          type="number"
                          step="0.01"
                          min="0"
                          className="input"
                          value={formData.lateFeeConfig.amount}
                          onChange={(e) => handleLateFeeChange('amount', parseFloat(e.target.value) || 0)}
                          placeholder={formData.lateFeeConfig.type === 'percentage' ? '5' : '25'}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="gracePeriod">Grace Period (days)</label>
                        <input
                          id="gracePeriod"
                          type="number"
                          min="0"
                          className="input"
                          value={formData.lateFeeConfig.gracePeriodDays}
                          onChange={(e) => handleLateFeeChange('gracePeriodDays', parseInt(e.target.value) || 0)}
                          placeholder="7"
                        />
                        <span className="help-text">Days after due date before fee applies</span>
                      </div>

                      <div className="form-group">
                        <label htmlFor="maxFee">Maximum Fee (optional)</label>
                        <input
                          id="maxFee"
                          type="number"
                          step="0.01"
                          min="0"
                          className="input"
                          value={formData.lateFeeConfig.maxFee}
                          onChange={(e) => handleLateFeeChange('maxFee', e.target.value ? parseFloat(e.target.value) : '')}
                          placeholder="No limit"
                        />
                        <span className="help-text">Cap the maximum late fee amount</span>
                      </div>
                    </div>

                    <div className="late-fee-preview">
                      <strong>Preview:</strong>{' '}
                      {formData.lateFeeConfig.type === 'percentage'
                        ? `${formData.lateFeeConfig.amount}% fee after ${formData.lateFeeConfig.gracePeriodDays} days`
                        : `${formData.currency} ${formData.lateFeeConfig.amount} fee after ${formData.lateFeeConfig.gracePeriodDays} days`}
                      {formData.lateFeeConfig.maxFee && ` (max ${formData.currency} ${formData.lateFeeConfig.maxFee})`}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Invoice & Billing Configuration */}
          <div className="form-section">
            <button
              type="button"
              className="section-toggle"
              onClick={() => setShowBillingConfig(!showBillingConfig)}
            >
              <span>{showBillingConfig ? '▼' : '▶'} Invoice & Billing Settings</span>
              <span className="toggle-hint">
                {formData.billingConfig.paymentOptionsEnabled ? 'Payment options enabled' : 'Default settings'}
              </span>
            </button>

            {showBillingConfig && (
              <div className="section-content">
                <p className="section-description">
                  Configure invoice settings, payment options, and monthly infrastructure billing for this client.
                </p>

                {/* Monthly Infrastructure Billing */}
                <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#0369a1', fontSize: '0.95rem' }}>Monthly Infrastructure Billing</h4>

                  <div className="form-group">
                    <label htmlFor="monthlyBillingType">Billing Type</label>
                    <select
                      id="monthlyBillingType"
                      className="input"
                      value={formData.billingConfig.monthlyBillingType}
                      onChange={(e) => handleBillingConfigChange('monthlyBillingType', e.target.value)}
                    >
                      <option value="actual_plus_percent">Actual GCP/Firebase + Management Fee %</option>
                      <option value="fixed">Fixed Monthly Amount</option>
                      <option value="included">Included in Retainer (no separate charge)</option>
                    </select>
                  </div>

                  {formData.billingConfig.monthlyBillingType === 'actual_plus_percent' && (
                    <div className="form-group">
                      <label htmlFor="managementFeePercent">Management Fee (%)</label>
                      <input
                        id="managementFeePercent"
                        type="number"
                        min="0"
                        max="100"
                        className="input"
                        value={formData.billingConfig.managementFeePercent}
                        onChange={(e) => handleBillingConfigChange('managementFeePercent', e.target.value)}
                        placeholder="20"
                      />
                      <span className="help-text">Added on top of actual cloud costs (default: 20%)</span>
                    </div>
                  )}

                  {formData.billingConfig.monthlyBillingType === 'fixed' && (
                    <div className="form-group">
                      <label htmlFor="monthlyFixedAmount">Fixed Monthly Amount ({formData.currency})</label>
                      <input
                        id="monthlyFixedAmount"
                        type="number"
                        min="0"
                        step="0.01"
                        className="input"
                        value={formData.billingConfig.monthlyFixedAmount}
                        onChange={(e) => handleBillingConfigChange('monthlyFixedAmount', e.target.value)}
                        placeholder="100"
                      />
                    </div>
                  )}
                </div>

                {/* Payment Options */}
                <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#fefce8', borderRadius: '8px', border: '1px solid #fde047' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#854d0e', fontSize: '0.95rem' }}>Payment Options</h4>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.billingConfig.paymentOptionsEnabled}
                        onChange={(e) => handleBillingConfigChange('paymentOptionsEnabled', e.target.checked)}
                      />
                      <span>Enable staggered payment options for invoices</span>
                    </label>
                  </div>

                  {formData.billingConfig.paymentOptionsEnabled && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <span className="help-text" style={{ display: 'block', marginBottom: '0.5rem' }}>
                        Select which payment options to offer this client:
                      </span>
                      {formData.billingConfig.paymentOptions.map((option) => (
                        <div key={option.id} style={{ marginBottom: '0.5rem' }}>
                          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={option.enabled}
                              onChange={() => handlePaymentOptionToggle(option.id)}
                            />
                            <span>{option.name}</span>
                            <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                              ({option.installments === 1 ? 'one-time' : `${option.installments} payments`})
                            </span>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Invoice CC Emails */}
                <div style={{ padding: '1rem', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#166534', fontSize: '0.95rem' }}>Invoice Email Settings</h4>

                  <div className="form-group">
                    <label>CC Recipients (always copied on invoice emails)</label>
                    {formData.billingConfig.invoiceCcEmails.map((email, index) => (
                      <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <input
                          type="email"
                          className="input"
                          value={email}
                          onChange={(e) => handleCcEmailChange(index, e.target.value)}
                          placeholder="email@example.com"
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => handleRemoveCcEmail(index)}
                          style={{ padding: '0.5rem 0.75rem' }}
                          disabled={formData.billingConfig.invoiceCcEmails.length === 1}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleAddCcEmail}
                      style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}
                    >
                      + Add CC Recipient
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : client ? 'Update Client' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientModal;
